import { retryUntilTimeoutOrSuccess } from './misc';
import type { JavascriptInterfaceObject, RequestMessageTransportResult, TransportInfo } from './type';
import { webkitGlobalMessageHandlerExists, webkitRequestMessageTransport } from './webkit';

function requestMessageTransportTypeJavascriptObject<HostMetadata, GuestMetadata>(
  javascriptInterface: JavascriptInterfaceObject,
  channelName: string,
  metadata: GuestMetadata,
): RequestMessageTransportResult<HostMetadata> | null {
  const result = (() => {
    try {
      return javascriptInterface.requestMessageTransport(channelName, JSON.stringify(metadata));
    } catch {
      return javascriptInterface.requestMessageTransport(channelName);
    }
  })();
  if (!result) return null;
  return JSON.parse(result) as RequestMessageTransportResult<HostMetadata>;
}

async function requestMessageTransportTypeWebkit<HostMetadata, GuestMetadata>(
  channelName: string,
  metadata: GuestMetadata,
): Promise<RequestMessageTransportResult<HostMetadata> | null> {
  return webkitRequestMessageTransport(channelName, metadata);
}

function checkInterfaceObjectExists(): boolean {
  if (window.PrexJavascriptInterface) return true;
  if (webkitGlobalMessageHandlerExists()) return true;
  return false;
}

async function requestMessageTransportType<HostMetadata, GuestMetadata>(
  channelName: string,
  metadata: GuestMetadata,
): Promise<RequestMessageTransportResult<HostMetadata> | null> {
  if (window.PrexJavascriptInterface) {
    return requestMessageTransportTypeJavascriptObject(window.PrexJavascriptInterface, channelName, metadata);
  }
  if (webkitGlobalMessageHandlerExists()) {
    return requestMessageTransportTypeWebkit(channelName, metadata);
  }
  return null;
}

export default async function discoverMessageTransportType<HostMetadata, GuestMetadata>(
  channelName: string,
  metadata: GuestMetadata,
  abortSignal: AbortSignal,
): Promise<TransportInfo<HostMetadata>> {
  if (abortSignal.aborted) throw abortSignal.reason;
  await retryUntilTimeoutOrSuccess({
    tryFn: checkInterfaceObjectExists,
    timeoutErrorFn: () => new InterfaceObjectNotFoundError(),
    abortSignal,
    interval: 40,
    timeout: 200,
  });
  const transportInfo = await retryUntilTimeoutOrSuccess({
    tryFn: async () => {
      const res = await requestMessageTransportType<HostMetadata, GuestMetadata>(channelName, metadata);
      if (res?.result === true) return res.transportInfo;
    },
    timeoutErrorFn: () => new DiscoverMessageTransportTimeoutError(),
    abortSignal,
    interval: 100,
    timeout: 1000,
  });
  return transportInfo;
}

export class InterfaceObjectNotFoundError extends Error {
  constructor() {
    super('PrexJavascriptInterface or global message handler not found.');
    this.name = 'InterfaceObjectNotFoundError';
  }
}

export class DiscoverMessageTransportTimeoutError extends Error {
  constructor() {
    super('discoverMessageTransport failed due to timeout.');
    this.name = 'DiscoverMessageTransportTimeoutError';
  }
}
